import Handlebars from "handlebars";

/**
 * Structured editing model for the `conditional` node. The node stores a raw
 * handlebars `{{#if …}}…{{/if}}` template string (so backend serialization is a
 * lossless identity); this module parses that string into editable branches for
 * the editor bubble and formats branches back into a template string.
 *
 * Only `{{#if}}` / `{{else if}}` / `{{else}}` are modelled. A condition is either
 * a bare variable (truthiness), a `(eq|ne var "value")` comparison, or — when it's
 * anything we don't recognise — an opaque raw expression the bubble exposes as a
 * free-text field. Bodies are raw markdown text, edited as-is.
 */

/** A single condition expression in one of the three shapes the bubble understands. */
export type ConditionExpr =
  // A bare variable's truthiness. `negate` flips it (`is false` → `(not var)`).
  | { kind: "var"; variable: string; negate?: boolean }
  | { kind: "compare"; variable: string; op: "eq" | "ne"; value: string }
  | { kind: "raw"; expression: string };

/** One branch of a conditional. `condition === null` marks the final `else`. */
export interface ConditionalBranch {
  /** The condition for an `if` / `else if`, or null for the `else` branch. */
  condition: ConditionExpr | null;
  /** Raw markdown body rendered when this branch is taken. */
  body: string;
}

export interface ParsedConditional {
  branches: ConditionalBranch[];
  /**
   * False when the template couldn't be parsed into the supported shape — the
   * bubble then degrades to a single raw-template text field instead of branches.
   */
  parseable: boolean;
}

type HbsNode = {
  type: string;
  loc?: hbs.AST.SourceLocation;
  [key: string]: unknown;
};

const buildLineOffsets = (source: string): number[] => {
  const offsets = [0];
  for (let i = 0; i < source.length; i += 1) {
    if (source[i] === "\n") {
      offsets.push(i + 1);
    }
  }
  return offsets;
};

const locToOffset = (
  lineOffsets: number[],
  position: { line: number; column: number }
): number => (lineOffsets[position.line - 1] ?? 0) + position.column;

/** Parse a single condition argument node into a structured `ConditionExpr`. */
const parseCondition = (
  param: HbsNode | undefined,
  source: string,
  lineOffsets: number[]
): ConditionExpr => {
  const sliceLoc = (loc: hbs.AST.SourceLocation): string =>
    source.slice(
      locToOffset(lineOffsets, loc.start),
      locToOffset(lineOffsets, loc.end)
    );

  if (param?.type === "PathExpression") {
    const path = param as unknown as { parts?: string[]; original?: string };
    if ((path.parts?.length ?? 0) === 1) {
      return { kind: "var", variable: path.original ?? path.parts?.[0] ?? "" };
    }
  }

  if (param?.type === "SubExpression") {
    const sub = param as unknown as {
      path?: { original?: string };
      params?: HbsNode[];
    };
    const op = sub.path?.original;
    const params = sub.params ?? [];
    if (
      (op === "eq" || op === "ne") &&
      params.length === 2 &&
      params[0]?.type === "PathExpression" &&
      params[1]?.type === "StringLiteral"
    ) {
      const variable =
        (params[0] as unknown as { original?: string }).original ?? "";
      const value =
        (params[1] as unknown as { value?: string }).value ?? "";
      return { kind: "compare", variable, op, value };
    }
    // `(eq var true)` / `(ne var false)` → the variable's truthiness; the false /
    // negated forms flip it. Seeded if/else row conditionals produce these.
    if (
      (op === "eq" || op === "ne") &&
      params.length === 2 &&
      params[0]?.type === "PathExpression" &&
      params[1]?.type === "BooleanLiteral"
    ) {
      const variable =
        (params[0] as unknown as { original?: string }).original ?? "";
      const literal =
        (params[1] as unknown as { value?: boolean }).value ?? true;
      const negate = op === "eq" ? !literal : literal;
      return negate ? { kind: "var", variable, negate } : { kind: "var", variable };
    }
    // `(not …)` → negate whatever the inner expression parses to: a var flips its
    // truthiness (`is false`), a comparison flips its operator. An inner raw stays
    // raw (fall through to the whole expression below).
    if (op === "not" && params.length === 1) {
      const inner = parseCondition(params[0], source, lineOffsets);
      if (inner.kind === "var") {
        return inner.negate
          ? { kind: "var", variable: inner.variable }
          : { kind: "var", variable: inner.variable, negate: true };
      }
      if (inner.kind === "compare") {
        return { ...inner, op: inner.op === "eq" ? "ne" : "eq" };
      }
    }
  }

  // Anything else (and/or, nested helpers, multi-segment paths) is kept verbatim.
  return {
    kind: "raw",
    expression: param?.loc ? sliceLoc(param.loc) : "",
  };
};

/** Slice a Program/inverse body's source and trim the surrounding block whitespace. */
const sliceBody = (
  loc: hbs.AST.SourceLocation | undefined,
  source: string,
  lineOffsets: number[]
): string => {
  if (!loc) {
    return "";
  }
  const raw = source.slice(
    locToOffset(lineOffsets, loc.start),
    locToOffset(lineOffsets, loc.end)
  );
  // Drop a single leading/trailing newline so a re-formatted block stays stable.
  return raw.replace(/^\n/, "").replace(/\n$/, "");
};

/**
 * Whether an `inverse` program is an `{{else if}}` chain (exactly one chained
 * if-block) rather than a plain `{{else}}` body. Either way the rendered handlebars
 * is identical; this only decides how the bubble presents the branch.
 */
const isElseIf = (inverse: HbsNode | undefined): boolean => {
  const body = (inverse as { body?: HbsNode[] } | undefined)?.body;
  return (
    !!body &&
    body.length === 1 &&
    body[0].type === "BlockStatement" &&
    (body[0] as unknown as { path?: { original?: string } }).path?.original ===
      "if"
  );
};

const walkIfBlock = (
  block: HbsNode,
  source: string,
  lineOffsets: number[],
  branches: ConditionalBranch[]
): void => {
  const params = (block.params as HbsNode[] | undefined) ?? [];
  branches.push({
    condition: parseCondition(params[0], source, lineOffsets),
    body: sliceBody(
      (block.program as { loc?: hbs.AST.SourceLocation } | undefined)?.loc,
      source,
      lineOffsets
    ),
  });

  const inverse = block.inverse as HbsNode | undefined;
  if (!inverse) {
    return;
  }
  if (isElseIf(inverse)) {
    const nested = (inverse as unknown as { body: HbsNode[] }).body[0];
    walkIfBlock(nested, source, lineOffsets, branches);
    return;
  }
  branches.push({
    condition: null,
    body: sliceBody(inverse.loc, source, lineOffsets),
  });
};

/**
 * Parse a stored conditional template into editable branches. Returns a single
 * raw branch with `parseable: false` if the template isn't a single supported
 * `{{#if}}` block, so the bubble can fall back to a plain template editor.
 */
export const parseConditionalTemplate = (
  template: string
): ParsedConditional => {
  const fallback: ParsedConditional = {
    branches: [{ condition: { kind: "raw", expression: "" }, body: template }],
    parseable: false,
  };

  let ast: hbs.AST.Program;
  try {
    ast = Handlebars.parse(template);
  } catch {
    return fallback;
  }

  const body = ast.body as unknown as HbsNode[];
  const block = body.find((node) => node.type === "BlockStatement");
  if (
    !block ||
    (block as unknown as { path?: { original?: string } }).path?.original !==
      "if"
  ) {
    return fallback;
  }

  const lineOffsets = buildLineOffsets(template);
  const branches: ConditionalBranch[] = [];
  walkIfBlock(block, template, lineOffsets, branches);
  return { branches, parseable: true };
};

/** Format a single condition expression back to its handlebars source. */
export const formatCondition = (condition: ConditionExpr): string => {
  switch (condition.kind) {
    case "var":
      return condition.negate
        ? `(not ${condition.variable})`
        : condition.variable;
    case "compare":
      return `(${condition.op} ${condition.variable} "${condition.value}")`;
    case "raw":
      return condition.expression;
  }
};

/**
 * Format editable branches back into a `{{#if}}…{{/if}}` template string. Block
 * conditionals keep their `{{#if}}` / `{{else}}` / `{{/if}}` markers on their own
 * lines so handlebars' standalone whitespace-stripping removes the whole line when
 * a branch is false — leaving no blank line. Inline conditionals stay compact.
 */
export const formatConditionalTemplate = (
  branches: ConditionalBranch[],
  options: { block: boolean }
): string => {
  const ifBranches = branches.filter((b) => b.condition !== null);
  const elseBranch = branches.find((b) => b.condition === null);
  // An if-less conditional is meaningless; guard against it producing `{{#if }}`.
  if (ifBranches.length === 0) {
    return "";
  }

  const sep = options.block ? "\n" : "";
  const parts: string[] = [];

  ifBranches.forEach((branch, index) => {
    const keyword = index === 0 ? "#if" : "else if";
    const condition = formatCondition(
      branch.condition ?? { kind: "raw", expression: "" }
    );
    parts.push(`{{${keyword} ${condition}}}`);
    parts.push(branch.body);
  });

  if (elseBranch) {
    parts.push("{{else}}");
    parts.push(elseBranch.body);
  }

  parts.push("{{/if}}");
  return parts.join(sep);
};

/** A fresh, empty inline conditional with a single unset `if` branch. */
export const EMPTY_CONDITIONAL_BRANCHES: ConditionalBranch[] = [
  { condition: { kind: "var", variable: "" }, body: "" },
];
